import { MutableRefObject, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { widgetHoverAtom } from "atoms/interaction";
import {
  ALL_WELL_STATUSES,
  EvaChart,
  WELL_STATUS_CURRENT_TITLE
} from "constants/charts.enums";
import { useChartsContext } from "contexts";
import {
  orderSeriesByLegend,
  resolveWellStatusSelection,
  wellStatusDisplayName,
  wellStatusOverTimeChartTitle
} from "entities/charts/eva/requests/wellStatus";
import { translateWellStatusCurrentResponseToOptions } from "entities/charts/eva/translators/wellStatusCurrent";
import { translateWellStatusOverTimeResponseToOptions } from "entities/charts/eva/translators/wellStatusOverTime";
import { useAtomValue } from "jotai";
import { updateChartByPath } from "store/features";
import { RootState } from "store/rootReducer";
import getChartScreenshotOptions from "utils/chart/getChartScreenshotOptions";
import formatPropertyName from "utils/column/formatPropertyName";
import {
  applyFieldNameMode,
  resolveSimplifyCustomFieldNames
} from "utils/column/getColumnLabelWithContext";

import useColumnsDictionary from "hooks/useColumnsDictionary";

import { IChartScreenshotSettings } from "models/chart";

import { useUserSettings } from "components/user/hooks";

import { isPointerOverChart } from "./echart/tooltipVisibility";
import { AxisKey, blankAxisNames, useHideAxisTitles } from "./echart/useHideAxisTitles";
import { useChartEntities } from "./useChartEntities";
import {
  useWellStatusLegendOrder,
  useWellStatusSeriesColorMap
} from "./useWellStatusLegendOrder";

/**
 * Builds and pushes the ECharts options for both Well Status charts —
 * the lean equivalent of the wells useChartOptions, mirroring
 * useRigFracChartOptions.
 *
 * Runs the SHARED getChartScreenshotOptions transform in screenshot mode so the
 * capture matches every other chart's look.
 */
export function useWellStatusChartOptions(
  chartInstanceRef: MutableRefObject<Nullable<echarts.ECharts>>,
  isInstanceReady: boolean
) {
  const { id, entityKind } = useChartsContext();
  const columnsDictionary = useColumnsDictionary(entityKind);
  const dispatch = useDispatch();
  const {
    chartType,
    apiResponse,
    screenshot,
    chartFocus,
    request,
    locks,
    dataTable,
    options: persistedOptions
  } = useChartEntities(id);
  // The two current-status modes come back in DIFFERENT shapes (one series per
  // bin vs one per status), and the response alone cannot reliably tell them
  // apart — a single-bin filtered result looks identical to a single-status
  // stacked one. The request that produced it can, so read the mode from there.
  const currentRequest = request as unknown as Maybe<{
    status?: string;
    allStatuses?: boolean;
    timeScale?: string;
    customRange?: { from?: string; to?: string };
  }>;
  // Over time, the entity request is the SAVED one until the controller's first
  // echo, so name the status the chart resolves to rather than a stale label.
  const requestStatus =
    chartType === EvaChart.WellStatusOverTime
      ? resolveWellStatusSelection(currentRequest?.status, false)
      : currentRequest?.status;
  const isAllStatuses = currentRequest?.allStatuses === true;
  // On-chart caption for the over-time chart, e.g. "Well Status vs Time
  // (Monthly)". Derived from the REQUEST's time scale, not the response, so the
  // caption always names the granularity the user picked.
  const overTimeTitle = wellStatusOverTimeChartTitle(
    currentRequest?.timeScale,
    currentRequest?.customRange
  );
  // The status's human name for the value-axis title. Stacked mode echoes the
  // SELECTION ("All Statuses") rather than the server's generic "Well Count":
  // the bar length there is a total across statuses, so no single status name is
  // correct, but the axis should still say what the user picked.
  const statusTitle = wellStatusDisplayName(
    isAllStatuses ? ALL_WELL_STATUSES : requestStatus
  );
  const { userAbbreviations } = useUserSettings();
  const abbreviations = userAbbreviations?.abbreviations ?? [];
  const legendOrder = useWellStatusLegendOrder();
  // Bin -> hex from the legend the user is looking at; see the states block below.
  // Empty once the user picks a palette for this chart, whose colours the server
  // has already applied — the chart focus decides, see the hook.
  const seriesColors = useWellStatusSeriesColorMap(chartFocus);
  const prevOptionsRef = useRef<string>("");

  const isScreenshotActive = screenshot?.active ?? false;
  const preset = screenshot?.properties?.preset;

  // The status button (and Status Over Time's time-scale button on the x-axis)
  // overlay these titles on hover; hide the titles while the buttons show (same
  // condition as WellStatusSelector's `visible`).
  const isHover = useAtomValue(widgetHoverAtom)?.[id] ?? false;
  const hideTitles = isHover && !isScreenshotActive && !dataTable?.active;
  const titleAxes: AxisKey[] =
    chartType === EvaChart.WellStatusCurrent ? ["xAxis"] : ["xAxis", "yAxis"];
  useHideAxisTitles(
    chartInstanceRef,
    hideTitles,
    titleAxes,
    persistedOptions,
    isScreenshotActive
  );
  // Re-run on ANY screenshot-settings change while capturing. The panel writes
  // the whole preset (title/axis text overrides, uppercase, font sizes and
  // family, grid-line toggles, spacing, dimensions), so keying the effect on
  // `isScreenshotActive` alone left every control dead until some unrelated
  // re-render happened to re-run the transform — the "titles don't apply in
  // realtime" report (the identical fix rig/frac took in EVA-76).
  // The abbreviation dictionary rides along: the transform consumes it (Use
  // Abbreviations) but it lives outside the preset.
  const screenshotSignature = isScreenshotActive
    ? `true:${JSON.stringify(preset ?? {})}:${JSON.stringify(abbreviations)}`
    : "false";

  // The group dimension's HUMAN name ("Resource Play"). The server only knows the
  // raw property ("Header.ResourcePlay") — the label lives on the focus's
  // IGroupBy.title — so resolve it here and let the translator prefer it. Follows
  // the same override-vs-global rule as useWellStatusController.
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  // EVA-127 FIX 4: the chart-focus group-by is the SANITIZED object — the feature
  // allow-list in DEFAULT_CHART_FOCUS_ENTITY.properties.groupBy omits subgroup,
  // defaultUnit and metadata, the three fields applyFieldNameMode rebuilds the
  // label from, so a restored dashboard would hand it nothing to re-add. Resolve
  // the real column from columnsDictionary by property, exactly as both
  // useChartLegendTitle hooks do, and fall back to the sanitized object on a
  // miss. useColumnsDictionary also returns `{}` on first render (it populates
  // in an effect), so the fallback is what paints first — benign, the effect
  // re-runs once columns load.
  const activeGroupBy = chartFocus?.active
    ? (chartFocus?.properties?.groupBy?.property &&
        columnsDictionary?.[
          formatPropertyName(chartFocus.properties.groupBy.property)
        ]) ||
      chartFocus?.properties?.groupBy
    : globalGroupBy;
  // EVA-127: route through the shared field-name interpreter, same as every
  // other legend/axis title — see the identical WellContribution fix in
  // useChartLegendTitle.tsx. Without this the category-axis name came
  // straight from the stored title, which updateGroupBy no longer bakes the
  // subgroup prefix or unit into, so a custom field's axis rendered bare
  // ("DML Oct 15" instead of "Debug: DML Oct 15 (m)"). `preset` (above) is the
  // same settings object useChartLegendTitle reads via useScreenshotContext —
  // see resolveSimplifyCustomFieldNames's docstring.
  const simplifyCustomFieldNames = resolveSimplifyCustomFieldNames(
    isScreenshotActive,
    preset
  );
  const focusTitle = activeGroupBy?.title
    ? (applyFieldNameMode(
        activeGroupBy.title,
        activeGroupBy,
        simplifyCustomFieldNames
      ) as Maybe<string>)
    : undefined;

  useEffect(() => {
    const instance = chartInstanceRef.current;
    if (!instance || instance.isDisposed() || !isInstanceReady) return;

    // No response yet (chart-type switch, or a request in flight): erase rather
    // than leave the previous chart's bars on the canvas. Same `setOption({},
    // notMerge)` idiom useRigFracChartOptions uses.
    if (!apiResponse) {
      instance.setOption({}, true);
      return;
    }

    // Colour CLIENT-side from the legend, falling back to the server's
    // `series.style.hexColor` for any bin the legend does not name (and for the
    // very first paint, before the map response lands).
    //
    // Relying on the server alone is what made re-ordering the focus bins
    // flicker: the new order applies to the chart the instant the legend changes
    // (legendOrder, below), but the colours could not follow until a fresh
    // aggregation returned — seconds on a large filter — so the chart sat in an
    // intermediate state with the new order and the old colours. Reading both
    // from the same legend makes them move together, in one frame.
    const states = {
      title: apiResponse.title,
      layout: apiResponse.layout,
      seriesColors,
      // Stack order, tooltip rows and the data table all follow SERIES order, so
      // aligning it with the legend is what makes the four agree. The one exception
      // is Others, which the over-time translator stacks at the bottom (EVA-410).
      series: orderSeriesByLegend(apiResponse.series ?? [], legendOrder),
      chartType,
      // Only the over-time translator reads these (y-axis min/max lock handles);
      // the current-status chart ships no locks.
      locks
    };

    const baseOptions =
      chartType === EvaChart.WellStatusCurrent
        ? translateWellStatusCurrentResponseToOptions(
            states as never,
            focusTitle,
            isAllStatuses,
            statusTitle,
            // The chart's own name. The over-time chart appends its granularity;
            // this one has none, so the name alone is the heading. Read from the
            // display constant, never the enum — the enum's value is a persisted
            // key and is deliberately not the caption.
            WELL_STATUS_CURRENT_TITLE
          )
        : translateWellStatusOverTimeResponseToOptions(
            states as never,
            statusTitle,
            overTimeTitle,
            focusTitle,
            currentRequest?.timeScale
          );

    // An empty response must ERASE the previous chart, not just skip the update.
    // The translators return {} when there are no series, and the wrapper draws
    // its "No Data Available" indicator OVER the canvas — so returning early here
    // left the last successful render's bars sitting under the message, reading
    // as real data for the new selection.
    if (!baseOptions || Object.keys(baseOptions).length === 0) {
      instance.setOption({}, true);
      return;
    }

    // Same argument list the rig/frac hook passes: no wells-specific extras
    // (type-well series, forecast toggles) apply to this chart.
    const options =
      isScreenshotActive && instance
        ? getChartScreenshotOptions(
            baseOptions as never,
            preset as IChartScreenshotSettings,
            instance,
            abbreviations,
            false,
            undefined,
            apiResponse,
            false,
            undefined
          )
        : hideTitles
        ? blankAxisNames(baseOptions, titleAxes)
        : baseOptions;

    // Tooltip stays suppressed while the pointer is outside this widget: an
    // `enterable` tooltip is re-shown by ECharts' _keepShow across a notMerge
    // rebuild, which is what left it stuck on widget→widget moves (EVA-6690).
    const optionsObject = options as GenericObject;
    const withTooltipGate = isPointerOverChart(id)
      ? optionsObject
      : {
          ...optionsObject,
          tooltip: { ...(optionsObject.tooltip as GenericObject), show: false }
        };

    instance.setOption(withTooltipGate as never, { notMerge: true });

    // Persist the LIVE axis options — never the screenshot-transformed ones.
    // Hover controls read this back: the time-scale selector for the real
    // nameGap, and the AxisContainer for the y-axis lock config. The transform
    // rewrites both (and drops the sliders), so persisting it mid-capture moved
    // the lock handles and mis-placed the axis-title overlay.
    //
    // Only the three sub-objects that are actually read back, not the whole
    // options tree: `series[].data` is every bin x bucket count, and this widget
    // can appear several times on one dashboard. Guarded on the serialised value
    // so an identical payload does not churn the store — same shape as
    // useRigFracChartOptions, plus `grid`, which the x-axis selector reads here.
    // Stamped with the chart type that PRODUCED these axes. The two Well Status
    // charts put different things on the same axis — "Time" on the over-time
    // chart's x, the status count on the current chart's — and this JSON survives
    // a chart-type switch, so a consumer reading it blind renders the previous
    // chart's axis title until a new response lands. That is the "Time" button
    // flashing on the Current chart before it becomes the status selector.
    // Consumers compare the stamp and fall back to their own label when it does
    // not match; see WellStatusSelector.
    const optionsJson = JSON.stringify({
      chartType,
      xAxis: baseOptions.xAxis,
      yAxis: baseOptions.yAxis,
      grid: baseOptions.grid
    });
    if (optionsJson !== prevOptionsRef.current) {
      prevOptionsRef.current = optionsJson;
      dispatch(updateChartByPath({ id, path: "options", value: optionsJson }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiResponse,
    chartType,
    isInstanceReady,
    screenshotSignature,
    id,
    isAllStatuses,
    statusTitle,
    overTimeTitle,
    legendOrder,
    // Repaint the moment the legend recolours, without waiting for a new response.
    seriesColors,
    // Masked today — changing the focus also lands a new apiResponse — but the
    // group-axis title is read from it, so depend on it rather than rely on that
    // coupling holding. A string, so no identity churn.
    focusTitle,
    // Re-render on a lock change: the AxisLocker writes the user's min/max into
    // the entity, and without this dep the axis would only pick it up on the
    // next response.
    locks
  ]);
}
